/**
 * /settings/vets — tenant vet/practice directory CRUD.
 *
 * Table view: practice / vet name / phone / email / active pet count / actions.
 * Inline add + edit form — no separate routes needed at this scope.
 *
 * Gated by `pets.configure` server-side; the UI renders a read-only view for
 * staff roles (pets.view only).
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { useConfirm } from '@/components/Confirm';
import { createVet, deactivateVet, listVets, updateVet } from '@/lib/api';
import type { CreateVetInput, Vet } from '@/lib/types';
import { PencilLine, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface VetFormState {
  practiceName: string;
  vetName: string;
  phone: string;
  email: string;
  emergencyPhone: string;
  addressLine1: string;
  city: string;
  postcode: string;
  country: string;
  notes: string;
}

const EMPTY_FORM: VetFormState = {
  practiceName: '',
  vetName: '',
  phone: '',
  email: '',
  emergencyPhone: '',
  addressLine1: '',
  city: '',
  postcode: '',
  country: 'GB',
  notes: '',
};

function formFromVet(v: Vet): VetFormState {
  return {
    practiceName: v.practiceName,
    vetName: v.vetName ?? '',
    phone: v.phone ?? '',
    email: v.email ?? '',
    emergencyPhone: v.emergencyPhone ?? '',
    addressLine1: v.addressLine1 ?? '',
    city: v.city ?? '',
    postcode: v.postcode ?? '',
    country: v.country ?? 'GB',
    notes: v.notes ?? '',
  };
}

function toInput(form: VetFormState): CreateVetInput {
  return {
    practiceName: form.practiceName.trim(),
    vetName: form.vetName.trim() || null,
    phone: form.phone.trim() || null,
    email: form.email.trim() || null,
    emergencyPhone: form.emergencyPhone.trim() || null,
    addressLine1: form.addressLine1.trim() || null,
    city: form.city.trim() || null,
    postcode: form.postcode.trim() || null,
    country: form.country.trim() || null,
    notes: form.notes.trim() || null,
  };
}

interface VetFormProps {
  initial?: VetFormState;
  onSave: (form: VetFormState) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}

function VetForm({ initial, onSave, onCancel, saving }: VetFormProps) {
  const [form, setForm] = useState<VetFormState>(initial ?? EMPTY_FORM);

  function set(key: keyof VetFormState, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave(form);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-lg border border-border bg-surface p-6"
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Practice name *</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.practiceName}
            onChange={(e) => set('practiceName', e.target.value)}
            required
            placeholder="Riverside Animal Clinic"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Vet name</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.vetName}
            onChange={(e) => set('vetName', e.target.value)}
            placeholder="Dr Smith"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Phone</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="+44 1234 000000"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Email</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="vets@clinic.example"
            type="email"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Emergency phone</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.emergencyPhone}
            onChange={(e) => set('emergencyPhone', e.target.value)}
            placeholder="+44 800 000000"
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Address</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.addressLine1}
            onChange={(e) => set('addressLine1', e.target.value)}
            placeholder="123 High Street"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">City</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Postcode</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.postcode}
            onChange={(e) => set('postcode', e.target.value)}
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Notes</label>
          <textarea
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !form.practiceName.trim()}
          className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export function VetsSettingsPage() {
  const { state } = useAuthContext();
  const caps = state.me?.capabilities ?? [];
  const editable = caps.includes('pets.configure') || caps.includes('*');
  const { confirm, ConfirmDialog } = useConfirm();

  const [vets, setVets] = useState<Vet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await listVets({ all: showAll });
      setVets(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  async function handleAdd(form: VetFormState) {
    setSaving(true);
    try {
      await createVet(toInput(form));
      setAddOpen(false);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(id: string, form: VetFormState) {
    setSaving(true);
    try {
      await updateVet(id, toInput(form));
      setEditingId(null);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(id: string) {
    const yes = await confirm({
      title: 'Deactivate this vet?',
      message: 'Existing pet links are preserved.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!yes) return;
    try {
      await deactivateVet(id);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Vets</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Practice and vet contact directory. Link vets to individual pets on the pet detail page.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded"
          />
          Show inactive
        </label>
        {editable && !addOpen && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" />
            Add vet
          </button>
        )}
      </div>

      {/* Add form */}
      {addOpen && editable && (
        <VetForm onSave={handleAdd} onCancel={() => setAddOpen(false)} saving={saving} />
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : vets.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No vets yet.{' '}
          {editable && !addOpen && (
            <button
              type="button"
              className="text-primary underline"
              onClick={() => setAddOpen(true)}
            >
              Add the first one.
            </button>
          )}
        </p>
      ) : (
        <div className="space-y-2">
          {vets.map((vet) => (
            <div
              key={vet.id}
              className={`rounded-lg border border-border bg-surface p-4 ${vet.isActive === 0 ? 'opacity-60' : ''}`}
            >
              {editingId === vet.id ? (
                <VetForm
                  initial={formFromVet(vet)}
                  onSave={(form) => handleEdit(vet.id, form)}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                />
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{vet.practiceName}</span>
                      {vet.isActive === 0 && (
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </div>
                    {vet.vetName && <p className="text-sm text-muted-foreground">{vet.vetName}</p>}
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                      {vet.phone && <span>{vet.phone}</span>}
                      {vet.email && <span>{vet.email}</span>}
                      {vet.emergencyPhone && <span>Emerg: {vet.emergencyPhone}</span>}
                      {(vet.city || vet.postcode) && (
                        <span>{[vet.city, vet.postcode].filter(Boolean).join(' ')}</span>
                      )}
                    </div>
                    {vet.notes && <p className="mt-1 text-xs text-muted-foreground">{vet.notes}</p>}
                  </div>
                  {editable && (
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        aria-label="Edit vet"
                        onClick={() => setEditingId(vet.id)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <PencilLine className="h-3.5 w-3.5" />
                      </button>
                      {vet.isActive === 1 && (
                        <button
                          type="button"
                          aria-label="Deactivate vet"
                          onClick={() => handleDeactivate(vet.id)}
                          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
